import { Autocomplete, Chip, TextField } from "@mui/material"
import type { TextFieldProps } from "@mui/material"

export interface Option {
  label: string
  value: string
}

interface MultiSelectProps {
  options: Option[]
  selected: string[]
  onChange: (selected: string[]) => void
  className?: string
  placeholder?: string
  emptyMessage?: string
}

/**
 * Multi-value picker — a searchable combobox that renders each chosen
 * option as a removable chip. Rebuilt on MUI `Autocomplete multiple`
 * (was a Radix Popover + cmdk Command); keeps the same
 * `{ value, selected, onChange }` contract so call sites are unchanged.
 */
export function MultiSelect({
  options,
  selected,
  onChange,
  className,
  placeholder = "Select items...",
  emptyMessage = "No item found.",
}: MultiSelectProps) {
  const selectedOptions = selected
    .map((v) => options.find((o) => o.value === v) ?? { label: v, value: v })

  return (
    <Autocomplete
      multiple
      {...(className !== undefined ? { className } : {})}
      options={options}
      value={selectedOptions}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      noOptionsText={emptyMessage}
      disableCloseOnSelect
      onChange={(_e, next) => onChange(next.map((o) => o.value))}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...chipProps } = getTagProps({ index })
          return <Chip key={key} size="small" label={option.label} {...chipProps} />
        })
      }
      renderInput={(params) => {
        // `AutocompleteRenderInputParams` is MUI's own bag designed to be
        // spread straight onto a TextField, but its `InputLabelProps` shape +
        // `size: "small" | undefined` field don't line up with TextField's
        // discriminated union under exactOptionalPropertyTypes. Narrow it to
        // TextFieldProps (the bag is valid at runtime) and pass placeholder.
        const inputProps = params as TextFieldProps
        return <TextField {...inputProps} placeholder={placeholder} />
      }}
    />
  )
}
